import * as Sentry from '@sentry/react';

/**
 * Sentry SDK initialization for DCACLab React frontend.
 *
 * DSN is read from a server-rendered <meta name="sentry-dsn"> tag
 * so the secret is never hardcoded in JS source.
 */

function detectEnvironment() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('.local')) {
    return 'development';
  }
  if (hostname.includes('staging') || hostname.includes('cap.dcaclab.com') || hostname === 'iweb.dcaclab.com') {
    return 'staging';
  }
  return 'production';
}

function getDsn() {
  const meta = document.querySelector('meta[name="sentry-dsn"]');
  return meta ? meta.getAttribute('content') : '';
}

function getRelease() {
  const meta = document.querySelector('meta[name="app-version"]');
  return meta ? meta.getAttribute('content') : undefined;
}

const dsn = getDsn();
const environment = detectEnvironment();
const isProd = environment === 'production';

// Only initialize if we have a DSN
if (dsn) {
  // Expose on window for console debugging on non-production
  if (!isProd) {
    window.__Sentry = Sentry;
  }

  Sentry.init({
    dsn: dsn,
    environment: environment,
    release: getRelease(),

    // Performance tracing
    tracesSampleRate: isProd ? 0.1 : 1.0,

    // Session Replay — disabled for now (per spec)
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.5,

    // Filter out noise before sending to Sentry
    beforeSend(event) {
      const message = event.message || '';

      // ResizeObserver loop errors — browser noise, non-actionable
      if (message.includes('ResizeObserver loop')) return null;

      // Cross-origin "Script error." with no useful info
      if (message === 'Script error.' && (!event.exception || !event.exception.values || event.exception.values.length === 0)) {
        return null;
      }

      // Third-party ad script noise
      if (message.includes('googlesyndication') || message.includes('doubleclick')) return null;

      // Haxe/OpenFL canvas errors — non-actionable, originate from compiled Haxe engine
      var haxeMessagePatterns = ['clientside', 'openfl', 'lime'];
      var haxeSourcePatterns = ['DCACLab', 'openfl', 'lime', 'haxe'];

      var isHaxeMessage = haxeMessagePatterns.some(function (p) { return message.toLowerCase().includes(p); });
      var isHaxeSource = false;
      if (event.exception && event.exception.values) {
        isHaxeSource = event.exception.values.some(function (v) {
          return v.stacktrace && v.stacktrace.frames && v.stacktrace.frames.some(function (f) {
            return haxeSourcePatterns.some(function (p) { return f.filename && f.filename.includes(p); });
          });
        });
      }
      if (isHaxeMessage || isHaxeSource) return null;

      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  });
}

/**
 * Report an error to Sentry (production) or console.error (development).
 * Designed to be passed directly as the `onError` prop of ErrorBoundary.
 *
 * @param {Error} error - The caught Error object
 * @param {Object} context - Additional context metadata (e.g., { section: 'Chart' })
 * @param {Object} [errorInfo] - React error info with componentStack
 */
export function reportError(error, context, errorInfo) {
  if (!error) return;

  if (!dsn) {
    console.error('[Sentry]', context || '', error, errorInfo?.componentStack || '');
    return;
  }

  Sentry.withScope(function (scope) {
    if (context) {
      if (typeof context === 'string') {
        scope.setTag('context', context);
      } else {
        Object.keys(context).forEach(function (key) {
          scope.setExtra(key, context[key]);
        });
        if (context.section) {
          scope.setTag('section', context.section);
        }
      }
    }

    if (errorInfo && errorInfo.componentStack) {
      scope.setExtra('componentStack', errorInfo.componentStack);
    }

    Sentry.captureException(error);
  });
}

/**
 * Add a breadcrumb for lab-specific events.
 *
 * @param {string} category - Breadcrumb category (e.g., 'lab', 'simulation', 'ai')
 * @param {string} message - Human-readable description
 * @param {Object} [data] - Additional structured data
 */
export function addBreadcrumb(category, message, data) {
  if (!dsn) return;

  Sentry.addBreadcrumb({
    category: category,
    message: message,
    level: 'info',
    data: data || undefined,
  });
}
